import React, { useEffect, useState, memo } from 'react';
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { FormWrapper } from './Wrappers';
import { apiGetForeignPaymentCodes } from './Api';

type Option = { label: string, value: number }

// Skatteverket's betalningskod: what a payment abroad is for. Visma requires one on every foreign
// supplier and stores it per supplier, so it is chosen here rather than configured once for the
// club — a single value for every supplier would misdescribe most of them. There is deliberately
// no default: the only generic code, 999, is expressly "endast i undantagsfall".
//
// It is an Autocomplete rather than a plain select because the list is ~96 entries: typing
// "reklam" or "440" narrows it immediately, which a dropdown of that length cannot.
function PaymentCodeSelectRaw({value, onChange, id}: {
  value: number | null,
  onChange: (newValue: number | null) => void,
  id?: string,
}) {
  const [options, setOptions] = useState<Option[]>([])

  useEffect(() => {
    let live = true
    // Fetched on mount — the dialog only renders this when the supplier is foreign, so a domestic
    // one never pays for the request.
    apiGetForeignPaymentCodes()
      .then(data => { if (live) setOptions(data.codes.map(c => ({value: c.code, label: `${c.code} · ${c.description}`}))) })
      .catch(() => { /* reported on the error bus by Api; the field just stays empty */ })
    return () => { live = false }
  }, [])

  const selected = options.find(option => option.value === value) ?? null
  return (
    <Autocomplete<Option>
      id={id}
      autoHighlight
      options={options}
      value={selected}
      onChange={(_event, option) => onChange(option === null ? null : option.value)}
      renderInput={(params) => <TextField {...params} error={value === null} size="small"/>}
      renderOption={(props, option) => <Box component="li" {...props}>{option.label}</Box>}
    />
  )
}

const PaymentCodeSelect = memo(FormWrapper({Component: PaymentCodeSelectRaw}))

export default PaymentCodeSelect
